/**
 * Blood Health AI — motion system.
 * 1. Header: toggles the glass "scrolled" state.
 * 2. Scroll reveal: tags section building blocks with .bh-anim and lets an
 *    IntersectionObserver fade/rise them in, staggered per container.
 * Applied entirely at runtime so a no-JS visit renders everything visible.
 */
(() => {
  // --- Header scroll state ---
  // Inner pages render the white pill server-side (bh-header--pill on the
  // <header>); the home page starts transparent over its dark hero and
  // morphs into the pill on scroll.
  const header = document.querySelector('.bh-header');
  if (header) {
    const pillAlways = header.classList.contains('bh-header--pill');
    const onScroll = () => {
      const scrolled = window.scrollY > 24;
      header.classList.toggle('bh-header--scrolled', scrolled);
      if (!pillAlways) header.classList.toggle('bh-header--pill', scrolled);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // --- Mobile drawer: close on Escape, backdrop tap, link nav, or leaving mobile ---
  const toggle = document.querySelector('.bh-header__toggle');
  if (toggle) {
    const close = () => {
      if (toggle.contains(document.activeElement)) {
        toggle.querySelector('summary')?.focus();
      }
      toggle.removeAttribute('open');
    };
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && toggle.hasAttribute('open')) close();
    });
    // Taps on the ::before backdrop dispatch to the <details> element itself
    toggle.addEventListener('click', (e) => {
      if (e.target === toggle) close();
    });
    toggle.querySelectorAll('a').forEach((a) => a.addEventListener('click', close));
    const mql = window.matchMedia('(min-width: 750px)');
    const onCross = (e) => {
      if (e.matches) close();
    };
    if (mql.addEventListener) mql.addEventListener('change', onCross);
    else if (mql.addListener) mql.addListener(onCross);
  }

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // --- Scroll reveal ---
  const SELECTORS = [
    '.bh-eyebrow',
    '.bh-card',
    '[class*="__media"]',
    '[class*="__collage"] > *',
    '[class*="__cards"] > *',
    '[class*="__grid"] > *',
    '[class*="__cta"]',
    '[class*="__actions"]',
    '[class*="__proof"]',
    '[class*="__text"]',
    '[class*="__body"]',
    '.bh-footer__brand',
    '.bh-footer__col',
    '.bh-footer__bottom',
  ].join(',');

  const all = Array.from(document.querySelectorAll(SELECTORS)).filter(
    (el) => !el.closest('.bh-header')
  );
  const pool = new Set(all);

  // Animate only outermost matches; nested matches ride along with their parent.
  const targets = all.filter((el) => {
    for (let p = el.parentElement; p; p = p.parentElement) {
      if (pool.has(p)) return false;
    }
    return true;
  });

  // Stagger siblings that share a container.
  const counts = new Map();
  targets.forEach((el) => {
    const i = counts.get(el.parentElement) || 0;
    counts.set(el.parentElement, i + 1);
    el.style.setProperty('--bh-anim-delay', `${Math.min(i * 90, 450)}ms`);
    el.classList.add('bh-anim');
  });

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('bh-anim--in');
          io.unobserve(entry.target);
        }
      }
    },
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
  );

  targets.forEach((el) => io.observe(el));
})();
